import * as THREE from 'three';
import { Entity, bossHpScale, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { Bullet } from './Bullet';
import { makeGlowSprite, pulseGlow } from './energyGlow';
import { useStore } from '../../state/store';

/**
 * Tesla Bastion — four-legged spider-walker with a tall Tesla coil on its back, defended
 * by 3 orbiting drone-coils that act as a rotating shield. While ANY drone is alive, the
 * coil is invulnerable. Once all 3 are destroyed, the central coil exposes itself.
 *
 * Threat: arcing lightning bolts at the player (visualized as fast bullets); proximity
 * lightning chains between Bastion and live drones (cosmetic).
 */
const COIL_HP = 50;
const DRONE_HP = 8;
const DRONE_COUNT = 3;
const DRONE_RADIUS = 22;
const SPEED = 9;
/** Whole-walker scale. The base mesh is built small (~16u tall); a wave-12 boss needs to loom like
 *  its wave-6/9 predecessors (80-96u sky craft), so it's scaled up to a ~42u towering strider.
 *  Uniform scale carries the mesh, drone-orbit radius AND the multi-part hit boxes together. */
const BASTION_SCALE = 2.4;
const SCORE = 7000;
const BOLT_INTERVAL = 1.6;
const BOLT_RANGE = 900;

interface DroneRef {
  id: string;
  group: THREE.Group;
  hp: number;
  destroyed: boolean;
  baseAngle: number;
  baseHeight: number;
  speed: number;
}

type Stage = 'walking' | 'detonating' | 'done';

export class TeslaBastion extends Entity {
  stage: Stage = 'walking';
  forward = new THREE.Vector3(0, 0, -1);
  readonly speed = SPEED;
  private coilHp = Math.round(COIL_HP * bossHpScale());
  private coil: THREE.Mesh;
  private coilCore: THREE.Mesh;
  private drones: DroneRef[] = [];
  private orbitTime = 0;
  private boltCooldown = 1.0;
  private currentTarget: { x: number; z: number } | null = null;
  private dieTimer = 0;
  private smokeTimer = 0;
  /** A few persistent line objects for arc visuals. We re-aim them each frame. */
  private arcLines: THREE.Line[] = [];

  constructor(spawnPos: THREE.Vector3) {
    super('tank');
    this.isBoss = true;
    this.hp = 999;
    this.radius = 18 * BASTION_SCALE; // footprint of the scaled-up strider
    this.group.position.copy(spawnPos); // spawnPos.y is the terrain surface (set by Game)

    const built = makeBastionMesh();
    this.group.add(built.mesh);
    this.coil = built.coil;
    this.coilCore = built.coilCore;
    this.drones = built.drones;
    for (const p of this.drones) p.hp = Math.round(p.hp * bossHpScale()); // later waves, tougher parts

    this.hitParts = [
      { id: 'coil', box: new THREE.Box3(new THREE.Vector3(-2, -2, -2), new THREE.Vector3(2, 4, 2)), object: this.coil },
      ...this.drones.map((d) => ({
        id: d.id,
        box: new THREE.Box3(new THREE.Vector3(-1.6, -1.6, -1.6), new THREE.Vector3(1.6, 1.6, 1.6)),
        object: d.group,
      })),
    ];

    // Arc-line material (additive so it glows over everything).
    const arcMat = new THREE.LineBasicMaterial({ color: 0x9bd6ff, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false });
    for (let i = 0; i < DRONE_COUNT; i++) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(2 * 3), 3));
      const line = new THREE.Line(geom, arcMat);
      this.arcLines.push(line);
      this.group.add(line);
    }
  }

  alive(): boolean { return this.stage === 'walking'; }

  damagePart(partId: string, amount: number, ctx: EntityCtx): void {
    if (this.stage !== 'walking') return;
    if (partId === 'coil') {
      // Invulnerable while any drone is still up — blue shield spark + name the weak point.
      if (this.drones.some((d) => !d.destroyed)) {
        const wp = new THREE.Vector3();
        this.coil.getWorldPosition(wp);
        ctx.game.shieldedFeedback(ctx, wp, 'SHIELDED — DESTROY THE ORBITING DRONES FIRST');
        return;
      }
      this.coilHp -= amount;
      const wp = new THREE.Vector3();
      this.coil.getWorldPosition(wp);
      new Explosion(wp, 5, 0.25, 'fire').addTo(ctx);
      if (this.coilHp <= 0) this.beginDying(ctx);
      return;
    }
    // (No body part — misses pass through.)
    const d = this.drones.find((dd) => dd.id === partId);
    if (d && !d.destroyed) {
      d.hp -= amount;
      const wp = new THREE.Vector3();
      d.group.getWorldPosition(wp);
      new Explosion(wp, 3, 0.2, 'fire').addTo(ctx);
      if (d.hp <= 0) {
        d.destroyed = true;
        new Explosion(wp, 22, 0.7, 'fire').addTo(ctx);
        new Explosion(wp, 16, 3, 'smoke').setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
        ctx.game.audio.playBoom(0.55);
        d.group.visible = false;
        if (this.hitParts) this.hitParts = this.hitParts.filter((p) => p.id !== d.id);
        // DISCHARGE: the broken drone's charge arcs out as a radial bolt ring — popping a drone
        // point-blank punishes the greedy; learn to break off as each one dies.
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2;
          const dir = new THREE.Vector3(Math.cos(a), 0.18, Math.sin(a)).normalize();
          ctx.game.add(new Bullet(wp.clone().addScaledVector(dir, 3), dir.clone().multiplyScalar(190), this.id, 'enemy'));
        }
        new Explosion(wp.clone(), 14, 0.25, 'shield').addTo(ctx);
      }
    }
  }

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    if (this.stage === 'detonating') { this.updateDying(ctx); return; }
    if (this.stage === 'done') { this.dead = true; return; }

    // Pulse coil core + its glow halo
    const core = this.coilCore.material as THREE.MeshBasicMaterial;
    const pulse = 0.6 + 0.4 * Math.sin(performance.now() * 0.012);
    core.color.setRGB(0.7 * pulse, 0.85, 1.0);
    if (this.coilCore.userData.glow) pulseGlow(this.coilCore.userData.glow as THREE.Sprite, performance.now() / 1000, 4, 0.22);

    // Walk toward villages
    if (!this.currentTarget) {
      const v = pickVillage(this.group.position, ctx);
      if (v) this.currentTarget = { x: v.position.x, z: v.position.z };
    }
    if (this.currentTarget) {
      const dx = this.currentTarget.x - this.group.position.x;
      const dz = this.currentTarget.z - this.group.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist > 1) {
        const fx = dx / dist, fz = dz / dist;
        this.forward.lerp(new THREE.Vector3(fx, 0, fz), 0.04).normalize();
        const yaw = Math.atan2(this.forward.x, this.forward.z);
        this.group.rotation.y = yaw;
        this.group.position.x += this.forward.x * SPEED * dt;
        this.group.position.z += this.forward.z * SPEED * dt;
      } else {
        this.currentTarget = null;
      }
    }
    // Keep the bastion seated on the rolling terrain as it repositions.
    this.group.position.y = ctx.game.gameScene.heightAt(this.group.position.x, this.group.position.z);

    // Orbit drones — each spins around the coil at its own speed/height.
    this.orbitTime += dt;
    for (const d of this.drones) {
      if (d.destroyed) continue;
      const a = d.baseAngle + this.orbitTime * d.speed;
      d.group.position.set(Math.cos(a) * DRONE_RADIUS, d.baseHeight + Math.sin(a * 0.7) * 1.5, Math.sin(a) * DRONE_RADIUS);
      d.group.rotation.y = -a;
    }
    // Arc lines from coil to live drones (additive blue glow).
    const coilWorld = new THREE.Vector3();
    this.coil.getWorldPosition(coilWorld);
    for (let i = 0; i < this.drones.length; i++) {
      const line = this.arcLines[i];
      const drone = this.drones[i];
      if (drone.destroyed) {
        line.visible = false;
        continue;
      }
      line.visible = true;
      const droneWorld = new THREE.Vector3();
      drone.group.getWorldPosition(droneWorld);
      const positions = (line.geometry.attributes.position as THREE.BufferAttribute);
      // Use coil/drone in scene-world space; line is parented to bastion's group, so
      // convert each endpoint into the group's local frame.
      this.group.updateWorldMatrix(true, false);
      const inv = new THREE.Matrix4().copy(this.group.matrixWorld).invert();
      const localCoil = coilWorld.clone().applyMatrix4(inv);
      const localDrone = droneWorld.clone().applyMatrix4(inv);
      positions.setXYZ(0, localCoil.x, localCoil.y, localCoil.z);
      positions.setXYZ(1, localDrone.x, localDrone.y, localDrone.z);
      positions.needsUpdate = true;
    }

    // Lightning bolt at the nearest friendly — armor pushed onto the bastion draws its fire.
    // OVERLOAD enrage: with its drone shield gone the exposed coil dumps charge twice as fast.
    const overloaded = this.drones.every((d) => d.destroyed);
    this.boltCooldown -= dt;
    const boltTarget = ctx.game.nearestFriendly(this.group.position, BOLT_RANGE);
    if (boltTarget && this.boltCooldown <= 0) {
      this.boltCooldown = (overloaded ? BOLT_INTERVAL * 0.5 : BOLT_INTERVAL) + Math.random() * 0.4;
      this.fireBolt(ctx, boltTarget.position);
    }

    // Body smoke vents
    this.smokeTimer += dt;
    if (this.smokeTimer >= 0.7) {
      this.smokeTimer = 0;
      const offLocal = new THREE.Vector3((Math.random() - 0.5) * 16, 11, (Math.random() - 0.5) * 16);
      offLocal.applyQuaternion(this.group.quaternion);
      new Explosion(this.group.position.clone().add(offLocal), 9, 1.8, 'smoke')
        .setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
    }
  }

  private fireBolt(ctx: EntityCtx, at: THREE.Vector3) {
    const muzzle = new THREE.Vector3();
    this.coil.getWorldPosition(muzzle);
    const dir = at.clone().sub(muzzle).normalize();
    dir.x += (Math.random() - 0.5) * 0.04;
    dir.y += (Math.random() - 0.5) * 0.04;
    dir.z += (Math.random() - 0.5) * 0.04;
    dir.normalize();
    const vel = dir.multiplyScalar(450);
    ctx.game.add(new Bullet(muzzle, vel, this.id, 'enemy'));
    new Explosion(muzzle, 3, 0.15, 'fire').addTo(ctx);
  }

  private beginDying(ctx: EntityCtx) {
    this.stage = 'detonating';
    this.dieTimer = 0;
    useStore.getState().registerKill(SCORE);
    const wp = new THREE.Vector3();
    this.coil.getWorldPosition(wp);
    new Explosion(wp, 60, 1.0, 'fire').addTo(ctx);
    ctx.game.audio.playHydrogenRoar();
  }

  private updateDying(ctx: EntityCtx) {
    this.dieTimer += ctx.dt;
    const t = this.dieTimer;
    // Coil dumps a vertical lightning bolt to the sky for 0.6s
    if (t < 0.6) {
      const wp = new THREE.Vector3();
      this.coil.getWorldPosition(wp);
      for (let h = 0; h < 8; h++) {
        if (Math.random() < 0.5) {
          new Explosion(wp.clone().add(new THREE.Vector3((Math.random() - 0.5) * 4, h * 30, (Math.random() - 0.5) * 4)), 8, 0.3, 'fire').addTo(ctx);
        }
      }
    }
    // Then big detonation
    if (t >= 0.6 && t < 0.7) {
      const center = this.group.position.clone();
      center.y = -460;
      new Explosion(center, 180, 1.0, 'fire').addTo(ctx);
      new Explosion(center, 100, 0.6, 'fire').addTo(ctx);
      ctx.game.audio.playBoom(1.4);
    }
    if (t > 0.7 && t < 4 && Math.random() < 0.3) {
      const center = this.group.position.clone();
      const off = new THREE.Vector3((Math.random() - 0.5) * 18, 5 + Math.random() * 14, (Math.random() - 0.5) * 18);
      new Explosion(center.add(off), 28, 4, 'smoke').setDrift(new THREE.Vector3(0, 12, 0)).addTo(ctx);
    }
    if (t > 4) this.stage = 'done';
  }
}

function pickVillage(from: THREE.Vector3, ctx: EntityCtx) {
  let best = null;
  let bestD = Infinity;
  for (const v of ctx.game.gameScene.villages) {
    if (v.destroyed) continue;
    const dx = v.position.x - from.x;
    const dz = v.position.z - from.z;
    const d = Math.hypot(dx, dz);
    if (d < bestD) { bestD = d; best = v; }
  }
  return best;
}

/* ---------------------- mesh ---------------------- */

interface BBuilt { mesh: THREE.Group; coil: THREE.Mesh; coilCore: THREE.Mesh; drones: DroneRef[]; }

export function makeBastionMesh(): BBuilt {
  const g = new THREE.Group();
  const armor = new THREE.MeshStandardMaterial({ color: 0x344048, roughness: 0.85 });
  const armorDark = new THREE.MeshStandardMaterial({ color: 0x1f262d, roughness: 0.9 });
  const copper = new THREE.MeshStandardMaterial({ color: 0xc97a3c, roughness: 0.45, metalness: 0.85 });
  const ironDark = new THREE.MeshStandardMaterial({ color: 0x252528, roughness: 0.45, metalness: 0.7 });
  const dim = new THREE.MeshStandardMaterial({ color: 0x100b08, roughness: 1 });
  const blue = new THREE.MeshBasicMaterial({ color: 0x9bd6ff });

  // Body — heavy hexagonal pod, lifted HIGH on long legs so the walker looms over the field.
  const BODY_Y = 13;
  const body = new THREE.Mesh(new THREE.CylinderGeometry(6, 7.5, 4.5, 6, 2), armor);
  body.position.set(0, BODY_Y, 0);
  g.add(body);
  // Underslung sensor/gun cluster beneath the body.
  const belly = new THREE.Mesh(new THREE.CylinderGeometry(3.4, 1.8, 3.2, 6), armorDark);
  belly.position.set(0, BODY_Y - 3.2, 0);
  g.add(belly);
  // Top dome.
  const dome = new THREE.Mesh(new THREE.SphereGeometry(4.6, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2), armorDark);
  dome.position.set(0, BODY_Y + 2, 0);
  g.add(dome);

  // 4 long articulated legs: hip near the body → knee splayed wide → foot on the ground. A
  // cylinder spans each segment (oriented hip→knee, knee→foot) for a proper striding silhouette.
  const UP = new THREE.Vector3(0, 1, 0);
  const legSeg = (from: THREE.Vector3, to: THREE.Vector3, r: number): THREE.Mesh => {
    const dir = to.clone().sub(from); const len = dir.length();
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, 10), ironDark);
    m.position.copy(from).add(to).multiplyScalar(0.5);
    m.quaternion.setFromUnitVectors(UP, dir.normalize());
    return m;
  };
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const hip = new THREE.Vector3(Math.cos(a) * 4, BODY_Y - 1, Math.sin(a) * 4);
    const knee = new THREE.Vector3(Math.cos(a) * 11.5, 6, Math.sin(a) * 11.5);
    const foot = new THREE.Vector3(Math.cos(a) * 13, -1.6, Math.sin(a) * 13);
    g.add(legSeg(hip, knee, 0.85));
    g.add(legSeg(knee, foot, 0.6));
    const kneeJoint = new THREE.Mesh(new THREE.SphereGeometry(1.1, 10, 8), copper);
    kneeJoint.position.copy(knee); g.add(kneeJoint);
    const footPad = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.0, 0.9, 12), ironDark);
    footPad.position.set(foot.x, foot.y - 0.35, foot.z); g.add(footPad);
  }

  // Tesla coil column rising from the dome — taller, with more rings for menace.
  const COIL_BASE_Y = BODY_Y + 4;
  const colBase = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.6, 2.4, 16), copper);
  colBase.position.set(0, COIL_BASE_Y, 0);
  g.add(colBase);
  for (let i = 0; i < 11; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.95 - i * 0.045, 0.15, 10, 24), copper);
    ring.rotation.x = Math.PI / 2;
    ring.position.set(0, COIL_BASE_Y + 1.6 + i * 0.62, 0);
    g.add(ring);
  }
  // Coil top bulb (kill-target).
  const COIL_TOP_Y = COIL_BASE_Y + 10;
  const coil = new THREE.Mesh(new THREE.SphereGeometry(1.9, 32, 24), dim);
  coil.position.set(0, COIL_TOP_Y, 0);
  g.add(coil);
  // Glowing inner core.
  const coilCore = new THREE.Mesh(new THREE.SphereGeometry(1.15, 32, 24), blue);
  coilCore.position.copy(coil.position);
  const coilGlow = makeGlowSprite(0x8fdcff, 6.5);
  coilCore.add(coilGlow);
  coilCore.userData.glow = coilGlow;
  g.add(coilCore);

  // 3 orbital drones — each is a small armored sphere with copper rings and a glow.
  const drones: DroneRef[] = [];
  for (let i = 0; i < DRONE_COUNT; i++) {
    const dGroup = new THREE.Group();
    const shell = new THREE.Mesh(new THREE.SphereGeometry(1.2, 24, 16), armor);
    dGroup.add(shell);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.15, 10, 24), copper);
    ring.rotation.x = Math.PI / 2;
    dGroup.add(ring);
    const ring2 = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.12, 8, 24), copper);
    ring2.rotation.y = Math.PI / 2;
    dGroup.add(ring2);
    // Tiny core glow
    const dGlow = new THREE.Mesh(new THREE.SphereGeometry(0.5, 16, 12), blue);
    dGroup.add(dGlow);
    g.add(dGroup);
    drones.push({
      id: `drone${i}`,
      group: dGroup,
      hp: DRONE_HP,
      destroyed: false,
      baseAngle: (i / DRONE_COUNT) * Math.PI * 2,
      baseHeight: 22 + i * 0.6,
      speed: 1.2 + i * 0.2,
    });
  }

  // Loom large: scale the whole walker up and lift it so the splayed feet still plant on the
  // terrain (the group origin sits at ground level; feet are ~3u below origin pre-scale).
  g.scale.setScalar(BASTION_SCALE);
  g.position.y = 2.4 * BASTION_SCALE; // foot pads bottom out ~2.4u below the origin pre-scale
  return { mesh: g, coil, coilCore, drones };
}
